import * as _ from 'lodash'
import React, { useState } from 'react';
import * as actions from '../actions';
import componentServices from '../componentServices';
import { useJobCostItemDragDrop } from '../draggableJobCostLineItem';
import valueFilters from '../valueFilters';
import globalRelay from '../globalRelay';
import util from '../util';
import keyboardUtil from 'Scripts/keyboardUtils';
import * as extensions from './extensions';
import { AsyncButton, CurrencyInput, Header, jobCostRowOwner, JobCostHiddenWhenLocked, PercentInput, PinnableStats, Radio } from './sharedComponents';

const _jobCostLineItemTypes = [{ name: 'glass' }, { name: 'special-order-glass' }];
const _inputQuantity = [['1', 1], ['2', 2], ['3', 3]];
const KEY_DOWN_CODES = [13];

const JobCostGlassRow = (extensions as any).JobCostGlassRow || function(props) {
	const { alwaysValidate, buttonHandler, classNames = [], draggable, buttonClass, dropLineItem, lineItem, updateModel } = props;
	const [shouldValidate, setShouldValidate] = useState(alwaysValidate);
	const [{isDragging, dragSourceRef, dragPreviewRef, dropItem, dropTargetRef, rowRef}] = useJobCostItemDragDrop({ 
		item: lineItem,
		handleDrop: (dragItem, dropItem) => dropLineItem(dragItem.key, dropItem.key) 
	});

	function handleUpdate (path, autoSave, e) {
		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel((model, updateCallback) => {
			model.setAt(path, newVal);

			updateCallback(model, path);
		}, { autoSave: autoSave });
	}

	function validate (property?) {
		if (!property || shouldValidate) {
			const result = !lineItem.test(property);

			return result;
		}
		return true;
	}

	function handleButtonClick (e) {
		e.preventDefault();

		const valid = validate();

		if (!valid) {
			setShouldValidate(true)
		}
		else {
			// this assumes that the add handler succeeds
			setShouldValidate(alwaysValidate);
		}

		buttonHandler(valid, e);
	}

	function updateQuantity (newButton) {
		updateModel((model, updateCallback) => {
			model.quantity = newButton.value;

			updateCallback(model, 'quantity');
		}, { autoSave: true });
	}

	let dropClass:any = null;
	const dragClass = isDragging ? 'disabled' : null;

	if (dropItem) {
		const dropIndex = lineItem.sortIndex;
		const dragIndex = dropItem.sortIndex;
		dropClass = (dropIndex > dragIndex) ? 'dropBottom' : 'dropTop';
	}

	const isAddedRow = classNames.indexOf('added') !== -1;
	const disableEntry = lineItem.glassTypeKey ? undefined : 'disabled';
	const disableEntryReadOnly = lineItem.glassTypeKey ? 'readOnly' : 'readOnly disabled';
	const rowClasses = _.concat(classNames, dropClass, dragClass);
	const dragHandle = (draggable) ? <button ref={ dragSourceRef } className="icon iconOnly drag"><span /></button> : null;

	const glassTypes = componentServices.store.getIn(['model', 'domainEntities']).glassTypes;
	const doorSizes = componentServices.store.getIn(['model', 'domainEntities']).doorSizes;

	const glassTypeOptions:JSX.Element[] = [];
	glassTypeOptions.push(<option key="unknown" value="">Select a Type</option>);
	_.forEach(glassTypes, glassType => {
		glassTypeOptions.push(<option key={glassType.key} value={glassType.key}>{glassType.name}</option>);
	});

	const doorSizeOptions:JSX.Element[] = [];
	doorSizeOptions.push(<option key="unknown" value="">Select a Size</option>);
	_.forEach(doorSizes, doorSize => {
		doorSizeOptions.push(<option key={doorSize.key} value={doorSize.key}>{doorSize.name}</option>);
	});

	const unitCost = util.formatAsCurrency(lineItem.cost);
	const quantitySpan = isAddedRow ? (<span className="readOnly radioValue">{lineItem.quantity}</span>) : null;
	const buttons = _inputQuantity.map(([text, value]) => (
		{ classNames: [], value: value, label: text, isSelected: lineItem.quantity === value }
	));

	return <tr ref={ rowRef } className={ rowClasses.join(' ') } key={lineItem.key}>
		<td />
		<td>{dragHandle}</td>
		<td className={(!validate('glassTypeKey') ? 'required' : undefined)}>
			<select value={lineItem.viewValue('glassTypeKey')} onChange={e => handleUpdate('glassTypeKey', true, e)}>
				{ glassTypeOptions }
			</select>
		</td>
		<td className={(!validate('doorSizeKey') ? 'required' : undefined)}>
			<select className={disableEntry} value={lineItem.viewValue('doorSizeKey')} onChange={e => handleUpdate('doorSizeKey', true, e)}>
				{ doorSizeOptions }
			</select>
		</td>
		<td>
			<input className={disableEntry} type="text" placeholder="Enter" autoCapitalize="" 
				autoCorrect="off" value={lineItem.viewValue('description')} 
				onChange={e => handleUpdate('description', false, e)} 
				onBlur={e => handleUpdate('description', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'cost'}><span className={disableEntryReadOnly}>{unitCost}</span></td>
		<td className={'centered'}>
			<Radio classNames={[disableEntry]} buttons={buttons} 
				onChange={updateQuantity}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, handleButtonClick(e))}/>
			{quantitySpan}
		</td>
		<td className={'cost'}><span className={disableEntryReadOnly}>{util.formatAsCurrency(lineItem.totalCost)}</span></td>
		<td className={'margin' + ' ' + (!validate('margin') ? 'required' : '')}>
			<PercentInput className={disableEntry} placeholder="20" 
				value={lineItem.viewValue('margin') } 
				onChange={e => handleUpdate('margin', false, e)} 
				onBlur={e => handleUpdate('margin', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'sell' + ' ' + (!validate('sellPrice') ? 'required' : '')}>
			<CurrencyInput className={disableEntry} placeholder="0.00" 
				value={lineItem.viewValue('sellPrice')} 
				onChange={e => handleUpdate('sellPrice', false, e)} 
				onBlur={e => handleUpdate('sellPrice', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td><button className={buttonClass} onClick={handleButtonClick}><span /></button></td>
		<td />
	</tr>;
}

const JobCostGlassSection = (extensions as any).JobCostGlassSection || jobCostRowOwner({ listAccessor: model => model.lineItems, listFilterThunkFactory: props => row => row.type === props.type }, class GlassSection extends React.Component<any,any> {
	constructor (props) {
		super(props);

		this.state = this.getStateFromProps(this.props);

		this.dropLineItem = this.dropLineItem.bind(this);
	}

	getStateFromProps (props) {
		const state = _.assign({}, this.state);
		if (!state.pendingLineItem || state.pendingLineItem.type !== props.type) {
			state.pendingLineItem = this.createPendingLineItem(props);
		}
		return state;
	}

	addLineItemHandler (lineItemKey, isValid) {
		if (!isValid) {
			return;
		}

		this.props.updateModel(model => {
			if (lineItemKey === this.state.pendingLineItem.key) {
				this.state.pendingLineItem.sortIndex = this.props.getNextSortIndex(model);
				model.lineItems.set(lineItemKey, this.state.pendingLineItem);
				this.props.sortRows(model);

				this.setState({ pendingLineItem: this.createPendingLineItem(this.props) });
			}
		}, { autoSave: true });
	}

	createPendingLineItem (props) {
		const { jobCost, type } = props;

		return jobCost.createLineItem({ type: type, margin: jobCost.margin, quantity: 1 });
	}

	updateModel (key, updater, options) {
		this.props.updateModel((jobCost, lineItemUpdateCallback) => {
			let lineItem = jobCost.lineItems.get(key);

			if ((!lineItem) && (key === this.state.pendingLineItem.key)) {
				lineItem = this.state.pendingLineItem;
				options.autoSave = false;
			}
			if (lineItem) {
				updater(lineItem, lineItemUpdateCallback);
			}
		}, options);
	}

	dropLineItem (sourceKey, targetKey) {
		this.props.updateModel(jobCost => {
			const sourceItem = jobCost.lineItems.get(sourceKey);
			const targetItem = jobCost.lineItems.get(targetKey);

			this.props.handleItemDrop(jobCost, sourceItem, targetItem);
		}, { autoSave: true });
	}

	render () {
		const { getRowClasses, jobCost, rowContextMenuHandler, type } = this.props;
		const lineItems = _.map(_.sortBy(jobCost.getLineItemsByType(type), 'sortIndex'), item => {
			return (
				<JobCostGlassRow key={item.rowKey} updateModel={this.updateModel.bind(this, item.key)} dropLineItem={ this.dropLineItem } buttonHandler={ rowContextMenuHandler.bind(this, item)} lineItem={item} classNames={ getRowClasses({ item: item }) } buttonClass="icon iconOnly contextMenu" draggable={true} alwaysValidate={true} workAreas={jobCost.workAreas} />
			);
		});

		return (

			<div className="inner">
				<table className="compact draggable" cellPadding="0" cellSpacing="0">
					<thead>
						<tr>
							<td />
							<td />
							<td><span className="readOnly">Type</span></td>
							<td><span className="readOnly">Door Size</span></td>
							<td><span className="readOnly">Cabinet Info</span></td>
							<td className="cost"><span className="readOnly">Unit Cost</span></td>
							<td className="centered quantity"><span className="readOnly">Quantity</span></td>
							<td className="cost"><span className="readOnly">Cost</span></td>
							<td className="margin"><span className="readOnly">Margin %</span></td>
							<td className="sell"><span className="readOnly">Sell</span></td>
							<td className="action" />
							<td />
						</tr>
					</thead>
					<tbody>
						<JobCostHiddenWhenLocked jobCost={ jobCost }><JobCostGlassRow key={this.state.pendingLineItem.key} updateModel={this.updateModel.bind(this, this.state.pendingLineItem.key)} buttonHandler={this.addLineItemHandler.bind(this, this.state.pendingLineItem.key)} lineItem={this.state.pendingLineItem} classNames={ ['addRow'] } buttonClass="icon iconOnly addItem" draggable={false} workAreas={jobCost.workAreas} /></JobCostHiddenWhenLocked>
						{ lineItems }
					</tbody>
				</table>
			</div>
		);
	}
});

const JobCostSpecialGlassRow = (extensions as any).JobCostSpecialGlassRow || function(props) {
	const { alwaysValidate, buttonHandler, classNames = [], draggable, buttonClass, dropLineItem, lineItem, updateModel } = props;
	const [shouldValidate, setShouldValidate] = useState(alwaysValidate);
	const [{isDragging, dragSourceRef, dragPreviewRef, dropItem, dropTargetRef, rowRef}] = useJobCostItemDragDrop({ 
		item: lineItem,
		handleDrop: (dragItem, dropItem) => dropLineItem(dragItem.key, dropItem.key) 
	});

	function handleUpdate (path, autoSave, e) {
		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;
		updateModel((model, updateCallback) => {
			model.setAt(path, newVal);

			updateCallback(model, path);
		}, { autoSave: autoSave });
	}

	function updateQuantity (newButton) {
		updateModel((model, updateCallback) => {
			model.quantity = newButton.value;

			updateCallback(model, 'quantity');
		}, { autoSave: true });
	}

	function validate (property?) {
		if (!property || shouldValidate) {
			const result = !lineItem.test(property);
			return result;
		}
		return true;
	}

	function handleButtonClick (e) {
		e.preventDefault();

		const valid = validate();

		if (!valid) {
			setShouldValidate(true);
		}
		else {
			// this assumes that the add handler succeeds
			setShouldValidate(alwaysValidate);
		}

		buttonHandler(valid, e);
	}

	let dropClass:any = null;
	const dragClass:any = isDragging ? 'disabled' : null;

	if (dropItem) {
		const dropIndex = lineItem.sortIndex;
		const dragIndex = dropItem.sortIndex;
		dropClass = (dropIndex > dragIndex) ? 'dropBottom' : 'dropTop';
	}

	const isAddedRow = classNames.indexOf('added') !== -1;
	const disableEntry = lineItem.itemNote ? undefined : 'disabled';
	const disableEntryReadOnly = lineItem.itemNote ? 'readOnly' : 'readOnly disabled';
	const rowClasses = _.concat(classNames, dropClass, dragClass);
	const dragHandle = (draggable) ? <button ref={ dragSourceRef } className="icon iconOnly drag"><span /></button> : null;
	const quantitySpan = isAddedRow ? (<span className="readOnly radioValue">{lineItem.quantity}</span>) : null;
	const buttons = _inputQuantity.map(([text, value]) => (
		{ classNames: [], value: value, label: text, isSelected: lineItem.quantity === value }
	));

	return <tr ref={ rowRef }className={ rowClasses.join(' ') } key={lineItem.key}>
		<td />
		<td>{dragHandle}</td>
		<td className={(!validate('itemNote') ? 'required' : undefined)}>
			<input className={''} type="text" placeholder="Enter" autoCapitalize="" 
				autoCorrect="off" value={lineItem.viewValue('itemNote')} 
				onChange={e => handleUpdate('itemNote', false, e)} 
				onBlur={e => handleUpdate('itemNote', true, e)} 
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={(!validate('orderNote') ? 'required' : undefined)}>
			<input className={disableEntry} type="text" placeholder="Enter" autoCapitalize="" 
				autoCorrect="off" value={lineItem.viewValue('orderNote')} 
				onChange={e => handleUpdate('orderNote', false, e)} 
				onBlur={e => handleUpdate('orderNote', true, e)} 
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'lwh' + ' ' + ((!validate('width') || !validate('height')) ? 'required' : '')}>
			<input className={disableEntry} type="text" placeholder="W" value={lineItem.viewValue('width')} 
				onChange={e => handleUpdate('width', false, e)} 
				onBlur={e => handleUpdate('width', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
			<input className={disableEntry} type="text" placeholder="H" value={lineItem.viewValue('height')} 
				onChange={e => handleUpdate('height', false, e)} 
				onBlur={e => handleUpdate('height', true, e)} 
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td><input className={disableEntry} type="text" placeholder="Enter" autoCapitalize="" 
			autoCorrect="off" value={lineItem.viewValue('description')} 
			onChange={e => handleUpdate('description', false, e)} 
			onBlur={e => handleUpdate('description', true, e)} 
			onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'cost' + ' ' + (!validate('cost') ? 'required' : '')}>
			<CurrencyInput className={disableEntry} type="text" placeholder="Enter" autoCapitalize="" 
				autoCorrect="off" value={lineItem.viewValue('cost')} 
				onChange={e => handleUpdate('cost', false, e)} 
				onBlur={e => handleUpdate('cost', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'centered'}><Radio classNames={[disableEntry]} buttons={buttons} onChange={updateQuantity}/>{quantitySpan}</td>
		<td className={'cost'}><span className={disableEntryReadOnly}>{util.formatAsCurrency(lineItem.totalCost)}</span></td>
		<td className={'margin' + ' ' + (!validate('margin') ? 'required' : '')}>
			<PercentInput className={disableEntry} type="text" placeholder="20" 
				value={lineItem.viewValue('margin')} 
				onChange={e => handleUpdate('margin', false, e)} 
				onBlur={e => handleUpdate('margin', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'sell' + ' ' + (!validate('sellPrice') ? 'required' : '')}>
			<CurrencyInput className={disableEntry} type="text" placeholder="0.00" 
				value={lineItem.viewValue('sellPrice')} 
				onChange={e => handleUpdate('sellPrice', false, e)} 
				onBlur={e => handleUpdate('sellPrice', true, e)} 
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/></td>
		<td className="action"><button className={buttonClass} onClick={handleButtonClick}><span /></button></td>
		<td />
	</tr>;
}

const JobCostSpecialGlassSection = (extensions as any).JobCostSpecialGlassSection || jobCostRowOwner({ listAccessor: model => model.lineItems, listFilterThunkFactory: props => row => row.type === props.type }, class SpecialGlassSection extends React.Component<any,any> {
	constructor (props) {
		super(props);

		this.state = this.getStateFromProps(this.props);

		this.dropLineItem = this.dropLineItem.bind(this);
	}

	getStateFromProps (props) {
		const state = _.assign({}, this.state);

		if (!state.pendingLineItem || state.pendingLineItem.type !== props.type) {
			state.pendingLineItem = this.createPendingLineItem(props);
		}

		return state;
	}

	addLineItemHandler (lineItemKey, isValid) {
		if (!isValid) {
			return;
		}

		this.props.updateModel(model => {
			if (lineItemKey === this.state.pendingLineItem.key) {
				this.state.pendingLineItem.sortIndex = this.props.getNextSortIndex(model);
				model.lineItems.add(this.state.pendingLineItem);
				this.props.sortRows(model);

				this.setState({ pendingLineItem: this.createPendingLineItem(this.props) });
			}
		}, { autoSave: true });
	}

	createPendingLineItem (props) {
		const { jobCost, type } = props;

		return jobCost.createLineItem({ type: type, margin: jobCost.margin, quantity: 1 });
	}

	updateModel (key, updater, options) {
		this.props.updateModel((jobCost, lineUpdateCallback) => {
			let lineItem = jobCost.lineItems.get(key);

			if ((!lineItem) && (key === this.state.pendingLineItem.key)) {
				lineItem = this.state.pendingLineItem;
				options.autoSave = false;
			}

			if (lineItem) {
				updater(lineItem, lineUpdateCallback);
			}
		}, options);
	}

	dropLineItem (sourceKey, targetKey) {
		this.props.updateModel(jobCost => {
			const sourceItem = jobCost.lineItems.get(sourceKey);
			const targetItem = jobCost.lineItems.get(targetKey);

			this.props.handleItemDrop(jobCost, sourceItem, targetItem);
		}, { autoSave: true });
	}

	render () {
		const { getRowClasses, jobCost, rowContextMenuHandler, type } = this.props;

		const lineItems = _.map(_.sortBy(jobCost.getLineItemsByType(type), 'sortIndex'), item => {
			return (
				<JobCostSpecialGlassRow key={item.rowKey} updateModel={this.updateModel.bind(this, item.key)} dropLineItem={ this.dropLineItem } buttonHandler={ rowContextMenuHandler.bind(this, item)} lineItem={item} classNames={ getRowClasses({ item: item }) } buttonClass="icon iconOnly contextMenu" draggable={true} alwaysValidate={true} workAreas={jobCost.workAreas} />
			);
		});

		return (<section>
			<header>
				<span>Special Order Glass</span>
			</header>
			<div className="inner">
				<table className="compact draggable" cellPadding="0" cellSpacing="0">
					<thead>
						<tr>
							<td />
							<td />
							<td><span className="readOnly">Type</span></td>
							<td><span className="readOnly">Supplier</span></td>
							<td className="lwh"><span className="readOnly">Approx. Size (WH)</span></td>
							<td><span className="readOnly">Cabinet Info</span></td>
							<td className="cost"><span className="readOnly">Unit Cost</span></td>
							<td className="centered quantity"><span className="readOnly">Quantity</span></td>
							<td className="cost"><span className="readOnly">Cost</span></td>
							<td className="margin"><span className="readOnly">Margin %</span></td>
							<td className="sell"><span className="readOnly">Sell</span></td>
							<td className="action" />
							<td />
						</tr>
					</thead>
					<tbody>
						<JobCostHiddenWhenLocked jobCost={ jobCost }><JobCostSpecialGlassRow key={this.state.pendingLineItem.key} updateModel={this.updateModel.bind(this, this.state.pendingLineItem.key)} buttonHandler={this.addLineItemHandler.bind(this, this.state.pendingLineItem.key)} lineItem={this.state.pendingLineItem} classNames={ ['addRow'] } buttonClass="icon iconOnly addItem" draggable={false} workAreas={jobCost.workAreas} /></JobCostHiddenWhenLocked>
						{ lineItems }
					</tbody>
				</table>
			</div>
		</section>);
	}
});

export default (extensions as any).JobCostEditGlass || class JobCostEditGlass extends React.Component<any,any> {
	constructor (props) {
		super(props);

		this.printReport = this.printReport.bind(this);
		this.pinStats = this.pinStats.bind(this);
	}

	printReport () {
		const { jobCost, fetchReport } = this.props;
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);
		const report = globalConfig.jobCostSettings.glass.hideInHouse ? 'outside-supplier' : 'glass';

		return fetchReport(`internal/${report}`);
	}

	pinStats () {
		actions.setPinnedStatState({ key: 'jcGlass', isPinned: true });
	}

	render () {
		const { jobCost, summary } = this.props;
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);
		const hasPinnedStats = !!componentServices.store.getIn(['viewState', 'pinnedStats', 'jcGlass']);
		const titlebarActions = [<AsyncButton className="pdf" asyncAction={ this.printReport } label="Print" />];
		const excludedLineItemTypes = (globalConfig.jobCostSettings.workflow && globalConfig.jobCostSettings.workflow.excludedLineItemTypes) || [];
		const lineItemTypes = _.filter(_jobCostLineItemTypes, t => excludedLineItemTypes.indexOf(t.name) === -1);
		let taxCost = 0;

		const sections = _.map(lineItemTypes, type => {
			if (type.name === 'glass') {
				taxCost += summary.calculatePreSubtotalTaxCost('order', type.name);
				return (
					<JobCostGlassSection key={type.name} updateModel={this.props.updateModel} jobCost={jobCost} type={type.name} />
				);
			}
			else {
				taxCost += summary.calculatePreSubtotalTaxCost('order', type.name);
				return (
					<JobCostSpecialGlassSection key={type.name} updateModel={this.props.updateModel} jobCost={jobCost} type={type.name} />
				);
			}
		});

		return (
			<section>
				<Header title="Glass" actions={ titlebarActions } />
				<div className="jobCostGlass">
					{sections}
					{ hasPinnedStats ? <section className="stats total pinnable out" /> : null }
					<PinnableStats isPinned={ hasPinnedStats }>
						<section className={ hasPinnedStats ? 'stats total compact' : 'stats total pinnable'}>
							{ hasPinnedStats ? null : <button className="icon iconOnly pinItem" onClick={ this.pinStats }><span /></button> }
							{ taxCost ? <div className="cost column">
								{ util.formatAsCurrency(taxCost) } <span>GLASS TAX COST</span>
							</div> : null
							}
							<div className="cost column">
								{ util.formatAsCurrency(summary.orderGlassSell + summary.orderSpecialGlassSell) } <span>GLASS SUBTOTAL</span>
							</div>
						</section>
					</PinnableStats>
				</div>
			</section>

		);
	}
};